import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { StripedDataGrid } from '../../components/DataGrid/StripedDataGrid';
import { GridActionsCellItem, itIT } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

const KilometersDataGrid = ({
  data,
  onEditClick,
  onDeleteClick,
  isReportConfirmed,
  isAdminViewingOther,
}) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      {
        field: 'date',
        headerName: 'Data',
        flex: 1.5,
      },
      {
        field: 'description',
        headerName: 'Descrizione',
        flex: 2,
      },
      {
        field: 'type',
        headerName: 'Tipologia spesa',
        flex: 1.5,
        renderCell: ({ value }) => t(value),
      },
      {
        field: 'kilometer_cost',
        headerName: 'Costo per Chilometro',
        flex: 1.5,
      },
      {
        field: 'kilometers',
        headerName: 'Chilometri',
        flex: 1.5,
      },
      {
        field: 'total_cost',
        headerName: 'Costo Totale',
        flex: 1.5,
        renderCell: ({ value }) => value.toFixed(2),
      },
      {
        field: 'actions',
        headerName: 'Azioni',
        type: 'actions',
        cellClassName: 'actions',
        width: 150,
        getActions: ({ id }) =>
          [
            <GridActionsCellItem
              icon={
                !isReportConfirmed && !isAdminViewingOther ? (
                  <EditOutlinedIcon />
                ) : (
                  <VisibilityOutlinedIcon />
                )
              }
              label="Edit"
              onClick={(event) => {
                event.stopPropagation();
                onEditClick(id);
              }}
              color="inherit"
              key={`edit_${id}`}
            />,
            !isReportConfirmed && !isAdminViewingOther && (
              <GridActionsCellItem
                icon={<DeleteOutlinedIcon />}
                label="Delete"
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteClick(id);
                }}
                color="inherit"
                key={`delete_${id}`}
              />
            ),
          ].filter(Boolean),
      },
    ],
    [onEditClick, onDeleteClick, isReportConfirmed, isAdminViewingOther, t]
  );
  const rows = useMemo(() => JSON.parse(JSON.stringify(data)), [data]);

  return (
    <StripedDataGrid
      localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
      rows={rows}
      columns={columns}
      density="standard"
      disableColumnMenu
      disableSelectionOnClick
      disableRowSelectionOnClick
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
      }
      getRowHeight={() => 'auto'}
      initialState={{
        sorting: {
          sortModel: [
            {
              field: 'name',
              sort: 'asc',
            },
          ],
        },
        pagination: {
          paginationModel: {
            pageSize: 100,
          },
        },
      }}
      pageSizeOptions={[25, 50, 100]}
      sx={{
        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
        '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
        '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
          py: '22px',
        },
      }}
    />
  );
};

KilometersDataGrid.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onEditClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  isReportConfirmed: PropTypes.bool,
  isAdminViewingOther: PropTypes.bool,
};

KilometersDataGrid.defaultProps = {
  onEditClick: () => {},
  onDeleteClick: () => {},
  isReportConfirmed: false,
  isAdminViewingOther: false,
};

export default KilometersDataGrid;
